import {
  Component,
  EventEmitter,
  Output,
  Input,
  inject,
  signal,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { MunicipioRepository } from '../../domain/service/municipio/municipio-repository';
import { BuscadorMunicipioComponent } from '../shared/buscadores/buscador-municipio.component';
import { Municipio } from '../../domain/model/municipio/municipio';
import { NomenclaturaDto } from '../../domain/shared/models/nomenclatura';
import {
  onlyNumbersKeypress,
  sanitizeNumbersOnly
} from '../../common/input-utils';

/**
 * ===============================================================================
 * 🚀 MIGRACIÓN COMPLETADA: Constantes → Props dinámicos desde componente padre
 * ===============================================================================
 *
 * ✅ ANTES - Constantes hardcoded:
 * const TIPOS_VIA = [...];
 * const APENDICES = [...];
 * const CARDINALIDADES = [...];
 *
 * ✅ AHORA - Datos dinámicos desde parent:
 * @Input() tiposVia: NomenclaturaDto[] = [];
 * @Input() apendices: NomenclaturaDto[] = [];
 * @Input() cardinalidades: NomenclaturaDto[] = [];
 *
 * 📡 FUENTE DATOS:
 * DireccionesPersonaComponent → nomenclaturaService → backend endpoints
 * - GET /api/nomenclatura/tipos-via
 * - GET /api/nomenclatura/apendices  
 * - GET /api/nomenclatura/cardinalidades
 *
 * ⚡ VENTAJAS:
 * - Elimina duplicación de código
 * - Centraliza carga de datos en parent component
 * - Permite consistencia entre FormateadorDireccion y DireccionesPersona
 * - Facilita testing y mantenimiento
 * ===============================================================================
 */

/**
 * Formateador de direcciones según estándares colombianos
 * Basado en: IngresarDireccionFormateadaViewImpl.java (legacy GWT)
 *
 * Formato: [TIPO_VIA] [NUMERO_1] [APENDICE_1] [CARDINALIDAD_1] # [NUMERO_2] [APENDICE_2] [CARDINALIDAD_2] - [NUMERO_PLACA] [COMPLEMENTO]
 *
 * Ejemplos:
 * - CL 45 A SUR # 23 B ESTE - 67 APTO 201
 * - AV 68 # 45 - 23
 * - CR 15 # 45 A - 67 LOCAL 2
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-formateador-direccion',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    DividerModule,
    TooltipModule,
    BuscadorMunicipioComponent
  ],
  template: `
    <p-dialog
      [(visible)]="visible"
      [header]="'Ingresar Dirección'"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '54rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      [draggable]="false"
      position="center"
      appendTo="body"
    >
      <!-- Formulario de Ingresar Dirección Formateada -->
      <div class="space-y-3">
        <!-- Vista Previa de la Dirección -->
        <div
          class="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded p-2"
        >
          <h6
            class="text-blue-700 dark:text-blue-300 font-semibold mb-1 text-sm"
          >
            <i class="pi pi-eye text-blue-600 dark:text-blue-400 mr-1"></i>
            Dirección Formateada:
          </h6>
          <div
            class="text-base font-mono bg-white dark:bg-gray-800 dark:text-gray-100 p-2 rounded border dark:border-gray-600 min-h-[32px] flex items-center"
          >
            {{
              direccionComputada() ||
                'Configure los campos para generar la dirección'
            }}
          </div>
        </div>

        <!-- Fila 1: Tipo Vía + Número 1 -->
        <div class="flex flex-col sm:flex-row gap-2 items-end w-full">
          <div class="field" style="flex: 1;">
            <label class="block text-xs font-medium text-gray-600 mb-1"
              >Tipo Vía *</label
            >
            <p-dropdown
              [(ngModel)]="formato.tipoVia"
              [options]="tiposVia"
              optionLabel="descripcion"
              optionValue="codigo"
              placeholder="Seleccione..."
              appendTo="body"
              [filter]="true"
              filterBy="descripcion"
              [style]="{ width: '100%', fontSize: '0.875rem', height: '36px' }"
              (onChange)="actualizarDireccion()"
            >
            </p-dropdown>
          </div>
          <div class="field w-full sm:w-[75px]">
            <label class="block text-xs font-medium text-gray-600 mb-1"
              >*</label
            >
            <input
              pInputText
              [(ngModel)]="formato.numero1"
              placeholder=""
              maxlength="10"
              [style]="{ width: '100%', fontSize: '0.875rem', height: '36px' }"
              (keypress)="onlyNumbersKeypress($event)"
              (input)="actualizarDireccion()"
              (change)="onNumeroInput($event, 'numero1')"
            />
          </div>
        </div>

        <!-- Fila 2: Apéndice 1 + Cardinalidad 1 + Número 2 -->
        <div class="flex flex-col sm:flex-row gap-2 items-end">
          <div class="field" style="flex: 1;">
            <label class="block text-xs font-medium text-gray-600 mb-1"
              >Letra</label
            >
            <p-dropdown
              [(ngModel)]="formato.apendice1"
              [options]="apendices"
              optionLabel="descripcion"
              optionValue="codigo"
              placeholder="Seleccione..."
              appendTo="body"
              [filter]="true"
              filterBy="descripcion"
              [style]="{ width: '100%', fontSize: '0.875rem', height: '36px' }"
              (onChange)="actualizarDireccion()"
            >
            </p-dropdown>
          </div>
          <div class="field" style="flex: 1;">
            <label class="block text-xs font-medium text-gray-600 mb-1"
              >Cuadrante</label
            >
            <p-dropdown
              [(ngModel)]="formato.cardinalidad1"
              [options]="cardinalidades"
              optionLabel="descripcion"
              optionValue="codigo"
              placeholder="Seleccione..."
              appendTo="body"
              [filter]="true"
              filterBy="descripcion"
              [style]="{ width: '100%', fontSize: '0.875rem', height: '36px' }"
              (onChange)="actualizarDireccion()"
            >
            </p-dropdown>
          </div>
          <div class="field w-full sm:w-[75px]">
            <label class="block text-xs font-medium text-gray-600 mb-1"
              ># *</label
            >
            <input
              pInputText
              [(ngModel)]="formato.numero2"
              placeholder=""
              maxlength="10"
              [style]="{ width: '100%', fontSize: '0.875rem', height: '36px' }"
              (keypress)="onlyNumbersKeypress($event)"
              (input)="actualizarDireccion()"
              (change)="onNumeroInput($event, 'numero2')"
            />
          </div>
        </div>

        <!-- Fila 3: Apéndice 2 + Cardinalidad 2 + Placa -->
        <div class="flex flex-col sm:flex-row gap-2 items-end">
          <div class="field" style="flex: 1;">
            <label class="block text-xs font-medium text-gray-600 mb-1"
              >Letra</label
            >
            <p-dropdown
              [(ngModel)]="formato.apendice2"
              [options]="apendices"
              optionLabel="descripcion"
              optionValue="codigo"
              placeholder="Seleccione..."
              appendTo="body"
              [filter]="true"
              filterBy="descripcion"
              [style]="{ width: '100%', fontSize: '0.875rem', height: '36px' }"
              (onChange)="actualizarDireccion()"
            >
            </p-dropdown>
          </div>
          <div class="field" style="flex: 1;">
            <label class="block text-xs font-medium text-gray-600 mb-1"
              >Cuadrante</label
            >
            <p-dropdown
              [(ngModel)]="formato.cardinalidad2"
              [options]="cardinalidades"
              optionLabel="descripcion"
              optionValue="codigo"
              placeholder="Seleccione..."
              appendTo="body"
              [filter]="true"
              filterBy="descripcion"
              [style]="{ width: '100%', fontSize: '0.875rem', height: '36px' }"
              (onChange)="actualizarDireccion()"
            >
            </p-dropdown>
          </div>
          <div class="field w-full sm:w-[75px]">
            <label class="block text-xs font-medium text-gray-600 mb-1"
              >*</label
            >
            <input
              pInputText
              [(ngModel)]="formato.numeroPlaca"
              placeholder=""
              maxlength="10"
              [style]="{ width: '100%', fontSize: '0.875rem', height: '36px' }"
              (keypress)="onlyNumbersKeypress($event)"
              (input)="actualizarDireccion()"
              (change)="onNumeroInput($event, 'numeroPlaca')"
            />
          </div>
        </div>

        <!-- Complemento -->
        <div class="field">
          <label class="block text-xs font-medium text-gray-600 mb-1"
            >Complemento</label
          >
          <input
            pInputText
            [(ngModel)]="formato.complemento"
            placeholder="APTO 201, CASA 2B, LOCAL 3, OFICINA 405, etc."
            maxlength="50"
            [style]="{ width: '100%', fontSize: '0.875rem', height: '36px' }"
            (input)="actualizarDireccion()"
          />
        </div>

        <!-- Mensaje de campos obligatorios -->
        <div class="text-xs text-gray-500 italic">
          Los campos marcados con * son obligatorios
        </div>
      </div>

      <!-- Botones de Acción -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cancelar"
            icon="pi pi-times"
            [outlined]="true"
            (onClick)="cancelar()"
          >
          </p-button>
          <p-button
            label="Limpiar"
            icon="pi pi-eraser"
            severity="secondary"
            [outlined]="true"
            (onClick)="limpiarFormulario()"
          >
          </p-button>
          <p-button
            label="Aplicar Dirección"
            icon="pi pi-check"
            [disabled]="!esValido()"
            (onClick)="aplicarDireccion()"
          >
          </p-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Modal Buscador de Municipio -->
    <app-buscador-municipio
      (municipioSeleccionado)="onMunicipioSeleccionado($event)"
    >
    </app-buscador-municipio>
  `
})
export class FormateadorDireccionComponent {
  // ✅ Propiedades dinámicas que reemplazan constantes hardcoded
  @Input() tiposVia: NomenclaturaDto[] = [];
  @Input() apendices: NomenclaturaDto[] = [];
  @Input() cardinalidades: NomenclaturaDto[] = [];

  @Output() direccionFormateada = new EventEmitter<{
    direccion: string;
    municipio?: Municipio;
    barrio?: string;
  }>();

  @ViewChild(BuscadorMunicipioComponent)
  buscadorMunicipio!: BuscadorMunicipioComponent;

  private readonly messageService = inject(MessageService);
  // Inyectando PUERTO (no adaptador) - Arquitectura Hexagonal
  private readonly municipioRepository = inject(MunicipioRepository);

  // Estado del modal
  visible = false;

  // Formulario de formato
  formato: {
    tipoVia: string | null;
    numero1: string;
    apendice1: string | null;
    cardinalidad1: string | null;
    numero2: string;
    apendice2: string | null;
    cardinalidad2: string | null;
    numeroPlaca: string;
    complemento: string;
  } = {
    tipoVia: null,
    numero1: '',
    apendice1: null,
    cardinalidad1: null,
    numero2: '',
    apendice2: null,
    cardinalidad2: null,
    numeroPlaca: '',
    complemento: ''
  };

  // Municipio y barrio
  municipioSeleccionado = signal<Municipio | undefined>(undefined);
  barrio = '';

  // Dirección formateada computada
  direccionComputada = signal<string>('');

  /**
   * Abre el modal formateador
   */
  abrir(
    direccionActual?: string,
    municipioActual?: Municipio,
    barrioActual?: string
  ): void {
    this.visible = true;

    // Limpiar formulario
    this.limpiarFormulario();

    // Si hay una dirección actual, intentar parsearla
    if (direccionActual) {
      this.parseExistingAddress(direccionActual);
    }

    if (municipioActual) {
      this.municipioSeleccionado.set(municipioActual);
    }

    if (barrioActual) {
      this.barrio = barrioActual;
    }

    this.actualizarDireccion();
  }

  /**
   * Cierra el modal
   */
  cerrar(): void {
    this.visible = false;
  }

  /**
   * Intenta parsear una dirección existente para rellenar el formulario
   * TODO: Implementar parser inteligente para direcciones mal formateadas
   */
  parseExistingAddress(direccion: string): void {
    // Por ahora, solo limpiamos y ponemos la dirección completa en complemento
    // En una implementación más avanzada, esto usaría regex para detectar componentes
    this.formato.complemento = direccion.trim();
  }

  /**
   * Actualiza la dirección formateada en tiempo real
   */
  actualizarDireccion(): void {
    const partes: string[] = [];

    // Parte 1: Tipo de vía + número principal
    if (this.formato.tipoVia && this.formato.numero1) {
      let primera = `${this.formato.tipoVia} ${this.formato.numero1}`;

      if (this.formato.apendice1) {
        primera += ` ${this.formato.apendice1}`;
      }

      if (this.formato.cardinalidad1) {
        primera += ` ${this.formato.cardinalidad1}`;
      }

      partes.push(primera);
    }

    // Parte 2: # + número de cruce
    if (this.formato.numero2) {
      let segunda = `# ${this.formato.numero2}`;

      if (this.formato.apendice2) {
        segunda += ` ${this.formato.apendice2}`;
      }

      if (this.formato.cardinalidad2) {
        segunda += ` ${this.formato.cardinalidad2}`;
      }

      if (partes.length > 0 && this.formato.numero2) {
        partes.push(segunda);
      }
    }

    // Parte 3: - + número de placa
    if (this.formato.numeroPlaca) {
      partes.push(`- ${this.formato.numeroPlaca}`);
    }

    // Parte 4: Complemento
    if (this.formato.complemento?.trim()) {
      partes.push(this.formato.complemento.trim());
    }

    const direccionCompleta = partes.join(' ').trim();
    this.direccionComputada.set(direccionCompleta);
  }

  esValido(): boolean {
    return !!(
      this.formato.tipoVia &&
      this.formato.numero1 &&
      this.formato.numero2
    );
  }

  limpiarFormulario(): void {
    this.formato = {
      tipoVia: null,
      numero1: '',
      apendice1: null,
      cardinalidad1: null,
      numero2: '',
      apendice2: null,
      cardinalidad2: null,
      numeroPlaca: '',
      complemento: ''
    };
    this.municipioSeleccionado.set(undefined);
    this.barrio = '';
    this.actualizarDireccion();
  }

  aplicarDireccion(): void {
    if (!this.esValido()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail:
          'Complete los campos obligatorios (Tipo Vía, Números principales y Municipio)'
      });
      return;
    }

    this.direccionFormateada.emit({
      direccion: this.direccionComputada(),
      municipio: this.municipioSeleccionado(),
      barrio: this.barrio || undefined
    });

    this.cerrar();
  }

  cancelar(): void {
    this.cerrar();
  }

  // === INPUT HANDLERS ===

  /** Handler para keypress - solo permite números */
  onlyNumbersKeypress = onlyNumbersKeypress;

  /** Handler para input numérico - sanitiza y actualiza dirección */
  onNumeroInput(
    event: Event,
    campo: 'numero1' | 'numero2' | 'numeroPlaca'
  ): void {
    const input = event.target as HTMLInputElement;
    input.value = sanitizeNumbersOnly(input.value);
    this.formato[campo] = input.value;
    this.actualizarDireccion();
  }

  // === MUNICIPIO SEARCH ===
  abrirBuscadorMunicipio(): void {
    this.buscadorMunicipio.abrir();
  }

  onMunicipioSeleccionado(municipio: Municipio): void {
    this.municipioSeleccionado.set(municipio);
  }
}
